import { computed, signal } from '@preact/signals-react';

import { OrderApi } from '../api';
import type { StatusEnum } from '../model';
import { OrderModel, plainToOrder, plainToOrderDetail } from '../model';

import { restaurantStore } from '@/entities/restaurant/store/restaurantStore';
import { refreshSessionToken } from '@/shared/lib/sessionToken';

type Nullable<T> = T | null;

interface PayOrder {
  order_id: number;
  payment_url: string;
}

class OrderStore {
  orderIdStatusModal = signal<string | null>(null);

  order = signal<Nullable<OrderModel>>(null);

  userOrders = signal<OrderModel[]>([]);

  userOrdersLoading = signal(false);

  pay = signal<Nullable<PayOrder>>(null);

  loading = signal(false);

  error = signal<Nullable<string>>(null);

  status = computed<Nullable<StatusEnum>>(
    () => this.order.value?.status ?? null
  );

  total = computed<number>(() => this.order.value?.total ?? 0);

  itemsCount = computed<number>(() => this.order.value?.itemsCount ?? 0);

  visibleOrders = computed(() =>
    this.userOrders.value.filter(
      (o) =>
        o.restaurant === restaurantStore.current.value?.id &&
        (o.status === 'ACCEPTED' || o.status === 'PAID')
    )
  );

  lastVisibleOrder = computed(
    () =>
      this.visibleOrders.value.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        }
        return 0;
      })[0]
  );

  orderStatusModalVisible = computed(() => !!this.orderIdStatusModal.value);

  orderStatusModalSelectedOrder = computed(() =>
    this.userOrders.value.find(
      (o) => o.id.toString() === this.orderIdStatusModal.value?.toString()
    )
  );

  openOrderStatusModal = (id: string | number | null) => {
    this.orderIdStatusModal.value = id ? id.toString() : null;
  };

  closeOrderStatusModal = () => {
    this.orderIdStatusModal.value = null;
  };

  hasUserOrders = computed(
    () => !this.userOrdersLoading.value && this.userOrders.value.length > 0
  );

  fetchUserOrders = async () => {
    if (this.userOrdersLoading.value) return;
    this.userOrdersLoading.value = true;
    try {
      const list = await OrderApi.listUserOrders();
      this.userOrders.value = list.map(plainToOrderDetail);
    } catch (e) {
      this.userOrders.value = [];
    } finally {
      this.userOrdersLoading.value = false;
    }
  };

  pollDetails = async (orderId: number) => {
    try {
      const plain = await OrderApi.get(orderId);
      const order = plainToOrder(plain);

      this.userOrders.value = this.userOrders.value.map((o) => {
        if (o.id === order.id) {
          return order;
        }
        return o;
      });

      return order;
    } catch (e) {
      console.error('error polling order', e);
    }
  };

  reset = () => {
    this.order.value = null;
    this.userOrders.value = [];
    this.userOrdersLoading.value = false;
  };

  create = async (restaurantId: number): Promise<PayOrder | null> => {
    this.loading.value = true;
    this.error.value = null;
    try {
      const pay = await OrderApi.create({ restaurant_id: restaurantId });
      this.pay.value = pay;

      await this.fetch(pay.order_id);
      return pay;
    } catch (e) {
      const err = e as Error & { response?: { data?: { detail?: string } } };
      this.error.value =
        err.response?.data?.detail || err.message || 'Не удалось создать заказ';
      throw err;
    } finally {
      this.loading.value = false;
      refreshSessionToken();
    }
  };

  tryCreate = async (restaurantId: number): Promise<PayOrder | null> => {
    if (this.loading.value) return null;
    return this.create(restaurantId);
  };

  fetch = async (orderId: number): Promise<OrderModel> => {
    this.loading.value = true;
    this.error.value = null;
    try {
      const detail = await OrderApi.getDetail(orderId);
      this.order.value = plainToOrderDetail(detail);
      return this.order.value;
    } catch (e) {
      const err = e as Error & { response?: { data?: { detail?: string } } };
      this.error.value =
        err.response?.data?.detail ||
        err.message ||
        'Не удалось получить заказ';
      throw err;
    } finally {
      this.loading.value = false;
    }
  };

  confirmPayment = async (orderId: number) => {
    this.loading.value = true;
    this.error.value = null;
    try {
      return await OrderApi.confirmPayment(orderId);
    } catch (e) {
      const err = e as Error & { response?: { data?: { detail?: string } } };
      this.error.value =
        err.response?.data?.detail ||
        err.message ||
        'Не удалось подтвердить оплату';
      throw err;
    } finally {
      this.loading.value = false;
    }
  };

  failPayment = async (orderId: number) => {
    this.loading.value = true;
    this.error.value = null;
    try {
      return await OrderApi.failPayment(orderId);
    } catch (e) {
      const err = e as Error & { response?: { data?: { detail?: string } } };
      this.error.value =
        err.response?.data?.detail ||
        err.message ||
        'Не удалось зафиксировать неуспех оплаты';
      throw err;
    } finally {
      this.loading.value = false;
    }
  };

  pollStatus = (orderId: number, intervalMs = 1000) => {
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      try {
        await this.fetch(orderId);
      } finally {
        if (!stopped) setTimeout(tick, intervalMs);
      }
    };
    tick();
    return () => {
      stopped = true;
    };
  };
}

export const orderStore = new OrderStore();
